import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Copy, Check, Link2, Tag } from 'lucide-react';
import { discountApi, type AffiliateInfo } from '@/services/discountApi';
import type { AxiosError } from 'axios';
import { Login } from '../auth/Login';

interface AffiliateReferralModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AffiliateReferralModal = ({ isOpen, onClose }: AffiliateReferralModalProps) => {
  const { t } = useTranslation();
  const [affiliateInfo, setAffiliateInfo] = useState<AffiliateInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [isUnauthorized, setIsUnauthorized] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadAffiliateInfo();
    }
  }, [isOpen]);

  const loadAffiliateInfo = async () => {
    setLoading(true);
    setIsUnauthorized(false);
    try {
      const info = await discountApi.getAffiliateInfo();
      setAffiliateInfo(info);
    } catch (err: unknown) {
      const axiosError = err as AxiosError;
      console.error('Failed to load affiliate info:', err);
      if (axiosError.response?.status === 401) {
        setIsUnauthorized(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    if (!affiliateInfo?.referralLink) return;
    try {
      await navigator.clipboard.writeText(affiliateInfo.referralLink);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const handleCopyCode = async () => {
    if (!affiliateInfo?.code) return;
    try {
      await navigator.clipboard.writeText(affiliateInfo.code);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  const handleLoginSuccess = () => {
    setIsUnauthorized(false);
    loadAffiliateInfo();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="relative w-full max-w-md rounded-lg bg-background p-6 shadow-xl border border-white/20">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-muted-foreground hover:text-foreground"
        >
          <X className="h-5 w-5" />
        </button>

        <h2 className="mb-2 text-xl font-semibold">
          {t('affiliate.modal.title', 'Your Affiliate Link')}
        </h2>
        <p className="mb-6 text-sm text-muted-foreground">
          {t(
            'affiliate.modal.description',
            'Share your link or promo code with your audience. They get 10% off, you earn 10% commission.'
          )}
        </p>

        {loading ? (
          <div className="flex h-32 items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
          </div>
        ) : affiliateInfo ? (
          <div className="space-y-6">
            {/* Referral Link */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium">
                <Link2 className="h-4 w-4 text-muted-foreground" />
                {t('affiliate.modal.referralLink', 'Referral Link')}
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={affiliateInfo.referralLink}
                  readOnly
                  className="flex-1 rounded-md border bg-muted px-3 py-2 text-sm"
                />
                <button
                  onClick={handleCopyLink}
                  className="flex items-center gap-2 rounded-md bg-foreground px-4 py-2 text-sm font-medium text-background hover:bg-foreground/90"
                >
                  {copiedLink ? (
                    <>
                      <Check className="h-4 w-4" />
                      {t('common.copied', 'Copied!')}
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4" />
                      {t('common.copy', 'Copy')}
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Promo Code */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium">
                <Tag className="h-4 w-4 text-muted-foreground" />
                {t('affiliate.modal.promoCode', 'Promo Code')}
              </label>
              <div className="flex gap-2">
                <div className="flex flex-1 items-center rounded-md border bg-muted px-3 py-2">
                  <span className="text-sm font-semibold tracking-wide">{affiliateInfo.code}</span>
                </div>
                <button
                  onClick={handleCopyCode}
                  className="flex items-center gap-2 rounded-md bg-foreground px-4 py-2 text-sm font-medium text-background hover:bg-foreground/90"
                >
                  {copiedCode ? (
                    <>
                      <Check className="h-4 w-4" />
                      {t('common.copied', 'Copied!')}
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4" />
                      {t('common.copy', 'Copy')}
                    </>
                  )}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                {t(
                  'affiliate.modal.promoCodeHint',
                  'Your promo code is the same as your referral code'
                )}
              </p>
            </div>

            {/* Info Box */}
            <div className="rounded-lg bg-muted p-4 text-sm">
              <p className="text-muted-foreground">
                {t(
                  'affiliate.modal.terms',
                  'Commission is earned on all paid orders from referred users. Payouts are processed monthly.'
                )}
              </p>
            </div>
          </div>
        ) : isUnauthorized ? (
          <div className="space-y-4">
            <Login 
              standalone 
              initialMode="login" 
              onSuccess={handleLoginSuccess} 
            />
          </div>
        ) : (
          <div className="space-y-4 text-center">
            <p className="text-muted-foreground">
              {t('affiliate.modal.error', 'Failed to load affiliate info. Please try again.')}
            </p>
            <button
              onClick={loadAffiliateInfo}
              className="rounded-md bg-foreground px-4 py-2 text-sm font-medium text-background hover:bg-foreground/90"
            >
              {t('common.retry', 'Retry')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
